import { Link, useNavigate } from "react-router";

import { Button } from "@/components/ui/button";
import { useHealth, useLogout, useMe } from "@/lib/api";

// Constant-length JSX expression keeps prettier output stable for any rendered name.
const PROJECT_NAME = "BOI Facilitation Quiz";

export function HomePage() {
  const navigate = useNavigate();
  const logoutMutation = useLogout();
  const { data: health } = useHealth();
  const { data: me } = useMe();

  const backendStatus = health?.status ?? "unreachable";

  const handleLogout = async () => {
    await logoutMutation.mutateAsync();
    navigate("/login");
  };

  return (
    <div className="bg-background min-h-screen">
      <header className="border-border flex items-center justify-between border-b px-6 py-4">
        <div>
          <p className="boi-eyebrow text-muted mb-2">Board of Innovation internal</p>
          <h1 className="boi-display font-heading text-primary text-2xl">{PROJECT_NAME}</h1>
        </div>
        <div className="flex items-center gap-3">
          <Link to="/profile">
            <Button variant="ghost" size="sm">
              Profile
            </Button>
          </Link>
          {me?.role === "admin" && (
            <>
              <Link to="/admin/users">
                <Button variant="ghost" size="sm">
                  Admin
                </Button>
              </Link>
              <Link to="/admin/activity">
                <Button variant="ghost" size="sm">
                  Activity
                </Button>
              </Link>
            </>
          )}
          <Button variant="outline" onClick={handleLogout}>
            Sign out
          </Button>
        </div>
      </header>
      <main className="mx-auto max-w-6xl px-6 py-12">
        <section className="boi-supergraphic bg-primary text-primary-foreground overflow-hidden rounded-[2rem] px-8 py-10 sm:px-10 sm:py-12">
          <div className="max-w-3xl">
            <p className="boi-eyebrow text-primary-foreground/70">Session control</p>
            <h2 className="boi-display font-heading mt-4 text-4xl leading-none sm:text-6xl">
              Create quiz moments that keep facilitation moving.
            </h2>
            <p className="mt-5 max-w-2xl text-sm leading-6 text-primary-foreground/72 sm:text-base">
              Use one internal platform to design prompts, pace group energy, and adapt each BOI
              session without spinning up a fresh client-specific build.
            </p>
          </div>
        </section>

        <section className="mt-8 grid gap-6 lg:grid-cols-[1.3fr_0.7fr]">
          <div className="rounded-[2rem] border border-border bg-card p-8">
            <p className="boi-eyebrow text-muted">What this workspace is for</p>
            <div className="mt-6 grid gap-5 md:grid-cols-2">
              <div>
                <h3 className="font-heading text-primary text-2xl">Author in-app</h3>
                <p className="text-muted mt-2 text-sm leading-6">
                  Build and iterate quiz flows directly in the platform so facilitators can refine
                  content between workshops.
                </p>
              </div>
              <div>
                <h3 className="font-heading text-primary text-2xl">Standardize the core</h3>
                <p className="text-muted mt-2 text-sm leading-6">
                  Keep recurring BOI formats consistent while allowing each session to flex around
                  the room.
                </p>
              </div>
              <div>
                <h3 className="font-heading text-primary text-2xl">Move at workshop speed</h3>
                <p className="text-muted mt-2 text-sm leading-6">
                  Reduce production overhead so teams can focus on facilitation quality, not setup.
                </p>
              </div>
              <div>
                <h3 className="font-heading text-primary text-2xl">See system health</h3>
                <p className="text-muted mt-2 text-sm leading-6">
                  Check environment readiness before a session starts and stay confident under time
                  pressure.
                </p>
              </div>
            </div>
          </div>

          <div className="rounded-[2rem] border border-border bg-card p-8">
            <p className="boi-eyebrow text-muted">Platform status</p>
            <div className="mt-6 space-y-5">
              <div>
                <p className="text-muted text-sm">Backend status</p>
                <p className="font-heading text-primary mt-1 text-3xl">{backendStatus}</p>
              </div>
              <p className="text-muted text-sm leading-6">
                Internal teams can use this area as a lightweight pre-flight check before going
                live with participants.
              </p>
              <p className="text-muted mt-2 text-sm">
            Backend status:{" "}
            <span className={backendStatus === "ok" ? "text-green-600" : "text-red-500"}>
              {backendStatus}
            </span>
              </p>
            </div>
          </div>
        </section>
      </main>
    </div>
  );
}
